// the signed-in user's view of the navigation: which groups, items and leaves they may see (permission,
// department scope and the `hidden` WIP flag), plus type-to-find across every visible leaf. Shared by the
// nav menu (compact bar, or the PC bar with the rail collapsed), the PC rail and the top-bar search, so
// all three always agree on what a user can reach.

import { useAuth } from '../auth/AuthContext.js';
import { NAV, type NavGroup, type NavItem } from './nav.js';

export interface VisibleNavGroup {
  group: NavGroup;
  /** the group's visible top-level items; a submenu parent is listed when any child is visible. */
  items: NavItem[];
}
export interface NavHit {
  leaf: NavItem;
  /** the group title, shown beside the hit ("Overview · Management"). */
  group: string;
}

export function useNavModel() {
  const { can, canSeePath } = useAuth();

  // A leaf is visible if the user has its permission(s). internal items also require the page to
  // be in department scope; external links (e.g. Gmail) just need the permission.
  const leafVisible = (i: NavItem) => {
    if (i.hidden) return false; // WIP - routed but kept out of the menu
    const permOk = i.anyPerm ? i.anyPerm.some((p) => can(p)) : !i.perm || can(i.perm);
    if (!permOk) return false;
    return i.external ? true : !!i.path && canSeePath(i.path);
  };
  // A submenu parent is visible if any of its children are visible.
  const itemVisible = (i: NavItem): boolean =>
    i.children ? i.children.some(leafVisible) : leafVisible(i);

  const groups: VisibleNavGroup[] = NAV.flatMap((group) => {
    if (group.perm && !can(group.perm)) return []; // group gated on a single permission
    const items = group.items.filter(itemVisible);
    return items.length ? [{ group, items }] : [];
  });

  // type-to-find: flatten every visible leaf (across groups + submenus) and match the query against
  // its label or its group name, so you can jump to any page without hunting through the groups.
  const findPages = (query: string): NavHit[] => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return NAV.flatMap((group) => {
      if (group.perm && !can(group.perm)) return [];
      return group.items
        .flatMap((i) => i.children ?? [i])
        .filter(leafVisible)
        .filter((l) => l.label.toLowerCase().includes(q) || group.title.toLowerCase().includes(q))
        .map((leaf) => ({ leaf, group: group.title }));
    });
  };

  return { leafVisible, itemVisible, groups, findPages };
}
